import React from 'react';
import { Link } from 'react-router-dom';
import {
  FlaskConical,
  Map as MapIcon,
  Mountain,
  Target,
  BarChart3,
  FileText,
  Sparkles,
  TrendingUp,
  Wrench,
  Activity,
  BookOpen,
  Lightbulb,
  ArrowRight,
  Heart,
  Layers,
  Grid3X3,
  Compass,
  Cpu,
  Network,
  Shapes,
  Wand2,
  Dice1,
  BookMarked,
  Palette,
  Eye,
  CheckCircle,
  Search,
  Trophy,
  Sliders,
  Video,
  FolderPlus,
  Globe,
  Image as ImageIcon,
  Shield,
  LayoutTemplate,
  LayoutGrid,
  MessageCircleQuestion,
  Sunrise,
  RefreshCw,
  Route,
  DollarSign,
  Users,
  Star,
  Scale,
  Brain,
  MessagesSquare,
  ClipboardList,
  FileCheck,
  FileStack,
} from 'lucide-react';
import { LAB_ROUTES, labRoutesMissingFromItems } from '../data/labRoutes';
import { LAB_CANDIDATES, labCandidatesByCategory, visibleLabCandidates } from '../data/labCandidates';
import { LAB_CATALOG_KINDS, resolveLabCatalogKind } from '../data/labCatalogKind';
import { isInPrioritiesProduct, PRIORITIES_PRODUCT_SIGNIFIER } from '../data/labPrioritiesProduct';
import { isLabItemNew, isLabItemUpdated } from '../data/labItemMeta';
import { useModuleGate } from '../lib/moduleGate';
import { isElevationOwnerOnlyPath, useIsElevationOwner } from '../lib/elevationAccess';
import { DEV_PORTS } from './siteMapShared';

const VISUAL_LANGUAGE_VIEWER_URL = `${import.meta.env.BASE_URL}visual-language/viewer.html`;
const VISUAL_LANGUAGE_SERVER_URL = `${DEV_PORTS.visualLanguageServer}/viewer.html`;
/** Same host as the app — local dev proxies /site-configurator/ to marketing-site-admin; production ships the built bundle under /site-configurator/. */
const SITE_BUILDER_URL = `${import.meta.env.BASE_URL}site-configurator/`;

/** Lab paths hidden while their workspace module is off. */
const DECISION_BRIEF_LAB_PATH = '/brief';

/** The guided tour walks the owner's own pages (About, Engines, Grammar, Models). */
const PRODUCT_TOUR_LAB_PATH = 'product-tour';

/** Whether a Lab entry is listed for this person: the owner's own pages
 *  (lib/elevationAccess.ts) and the tour through them are listed only for the
 *  owner; the rest of the team would be sent to the dashboard by them. */
function listedFor(elevationOwner: boolean, path: string): boolean {
  return elevationOwner || (!isElevationOwnerOnlyPath(path) && path !== PRODUCT_TOUR_LAB_PATH);
}

export const Lab: React.FC = () => {
  // The Decision Log (Brief App) is a module: on by default, and a workspace
  // admin can switch it off (owner ruling 2026-10-14) — no Lab entry while off.
  const decisionBriefEnabled = useModuleGate('decision_brief');
  const elevationOwner = useIsElevationOwner();

  // Lab items organized by target engine
  const labItems = [
    // Standalone Applications
    {
      path: SITE_BUILDER_URL,
      label: 'Site Builder',
      icon: LayoutTemplate,
      color: 'primary',
      description:
        'Compose marketing-site pages (sections + nav). Ships with the priorities SPA at /site-configurator/ — opens in a new tab on localhost and in production.',
      isStandalone: true,
      isExternal: true
    },
    {
      path: '/prioritization',
      label: 'Prioritization App',
      icon: Trophy,
      color: 'primary',
      description: 'Complete team prioritization workflow with item intake → item work → priority (contextual), role-based permissions, and all 12 System Modeling Spaces',
      isFeatured: true,
      isStandalone: true
    },
    {
      path: '/planning',
      label: 'Planning App',
      icon: ClipboardList,
      color: 'primary',
      description:
        'Planning mode — work graph, spreadsheet workbench, capacity, milestones, and plan-of-record alignment for sequenced delivery.',
      isFeatured: true,
      isStandalone: true,
    },
    {
      path: '/apps/mandate',
      label: 'Mandate App',
      icon: FileCheck,
      color: 'primary',
      description:
        'Transition governance — situation → outcome mandate framing, priorities stack, stakeholders, and decision record.',
      isFeatured: true,
      isStandalone: true,
    },
    {
      path: '/brief',
      label: 'Brief App',
      icon: FileStack,
      color: 'primary',
      description:
        'Decision briefs — async stakeholder intake, proposal comparison, and a decision panel for structured alignment.',
      isFeatured: true,
      isStandalone: true,
    },
    {
      path: '/elevation/workbook',
      label: 'Managing Priorities Workbook',
      icon: BookOpen,
      color: 'primary',
      description: 'Digital workbook content for managing priorities and decision-making processes',
      isStandalone: true
    },
    {
      path: '/elevation/home',
      label: 'Elevation',
      icon: Globe,
      color: 'primary',
      description: 'Your knowledge system: the four layers (M3 to M0), the stages and the engines. Only you see this.',
      isStandalone: true
    },
    {
      path: '/elevation/lab/outcome-driver-generator',
      label: 'Outcome Driver Generator',
      icon: Target,
      color: 'primary',
      description: 'Create compelling reasons for change with structured Outcome Drivers. Two modes: Quick Driver for fast single-driver creation, and Set Builder for building complementary sets with collaboration.',
      isStandalone: true,
      isFeatured: true
    },
    {
      path: '/elevation/lab/tool-selector',
      label: 'Prioritization Tool Selector',
      icon: Sliders,
      color: 'primary',
      description: 'Help select the right prioritization tool (Eisenhower Matrix, RICE, ICE, MoSCoW, Value vs Effort, etc.) based on your situation, constraints, and goals',
      isStandalone: true
    },
    {
      path: '/elevation/lab/clarity-compass',
      label: 'Clarity Compass',
      icon: Compass,
      color: 'primary',
      description: 'Diagnose shared reality integrity across seven subdomains (Context, Intent, Meaning, Role, Priority, Signal, Action) and three tiers (Individual, Interpersonal, Collective). Pre-prioritization diagnostic tool.',
      targetEngine: 'Self-discovery & Clarity',
      isFeatured: true,
      isCoachTool: true,
    },
    {
      path: '/elevation/ladder-of-inference',
      label: 'Ladder of Inference',
      icon: Layers,
      color: 'primary',
      description:
        'Climb from observable data to action — see where you select, interpret, assume, and conclude. Separate facts from judgments.',
      targetEngine: 'Self-discovery & Clarity',
      isFeatured: true,
      isCoachTool: true,
    },
    {
      path: '/elevation/johari-window',
      label: 'Johari Window',
      icon: Users,
      color: 'primary',
      description:
        'Known and unknown self — open, blind, hidden, and unknown quadrants for feedback, disclosure, and team trust.',
      targetEngine: 'Self-discovery & Clarity',
      isFeatured: true,
      isCoachTool: true,
    },
    {
      path: '/prioritization/tools',
      label: 'Prioritization Tools',
      icon: Sliders,
      color: 'primary',
      description: 'Access all prioritization tools and frameworks in one place: Stack Ranking, Pairwise Ranking, Dot Voting, RICE Scoring, Eisenhower Matrix, MoSCoW Method, and more. Find the right tool for your prioritization needs.',
      isStandalone: true,
      isFeatured: true
    },
    {
      path: '/elevation/bounded-agency-delegation',
      label: 'Bounded Agency Delegation',
      icon: Shield,
      color: 'primary',
      description: 'A leadership protocol for delegating to humans and AI. Explore the seven levels of bounded agency, the agency × risk/reversibility matrix, and build a delegation brief that generates the leader\'s delegation sentence.',
      isStandalone: true,
      isFeatured: true,
      isCoachTool: true,
    },
    {
      path: '/elevation/lab/delegation-authority',
      label: 'Delegation Authority Lab',
      icon: Shield,
      color: 'primary',
      description:
        'v0.1 — Guided wizard: place the work, set the risk envelope, choose authority on the ladder, and generate a bounded-agency brief for humans or AI.',
      targetEngine: 'Self-discovery & Clarity',
      isFeatured: true,
      isCoachTool: true,
    },
    {
      path: '/elevation/field-of-play',
      label: 'Field of Play',
      icon: Grid3X3,
      color: 'primary',
      description: 'A coach\'s clipboard for business: a stable field (Signal → Learn × Organization → Individual) where you place typed pieces and position carries meaning. The field reads itself back, flags anti-patterns, and suggests the plays to run next.',
      isStandalone: true,
      isFeatured: true,
      isCoachTool: true,
    },
    {
      path: '/elevation/inquiry-sequence',
      label: 'Inquiry Sequence',
      icon: MessageCircleQuestion,
      color: 'plum',
      description: 'The order of questions — and why I ask each one. A coach\'s flow for surfacing the controlling question through disciplined inquiry, with single-pass and three-lap field guides.',
      targetEngine: 'Self-discovery & Clarity',
      isFeatured: true,
      isCoachTool: true,
    },
    {
      path: '/elevation/lab/understanding-your-values',
      label: 'Understanding Your Values',
      icon: Heart,
      color: 'primary',
      description:
        'v0.2 — Discover your values from disgust, not aspiration. Start with experiences that revolted you; the opposite is the value.',
      targetEngine: 'Self-discovery & Clarity',
      isFeatured: true,
    },
    {
      path: '/elevation/lab/changing-unwanted-behaviors',
      label: 'Changing Unwanted Behaviors',
      icon: RefreshCw,
      color: 'primary',
      description:
        'v0.1 — Reprogram unwanted patterns from behavior → beliefs → thoughts. Six-question worksheet with fear challenge and realistic change timelines.',
      targetEngine: 'Self-discovery & Clarity',
      isFeatured: true,
    },
    {
      path: '/elevation/lab/logical-levels-story',
      label: 'DILTS Logical Levels',
      icon: Layers,
      color: 'primary',
      description:
        'v0.1 — Tell your story at every level (environment → purpose). Build pitches for behavior-depth small talk and values-depth interviews.',
      targetEngine: 'Self-discovery & Clarity',
      isFeatured: true,
    },
    {
      path: '/elevation/lab/level-aware-thinking',
      label: 'Level-Aware Thinking',
      icon: Compass,
      color: 'primary',
      description:
        'v0.1 — Notice unintended level shifts in conversation. Diagnose altitude hijacks, re-anchor at the question\'s level, rebuild coherence.',
      targetEngine: 'Self-discovery & Clarity',
      isFeatured: true,
    },
    {
      path: '/elevation/lab/morning-boot',
      label: 'Morning Boot Routine',
      icon: Sunrise,
      color: 'primary',
      description:
        'Daily solo prioritization ritual — purge your plate, tag avoidances and North Star steps, rank top three, timebox, and focus.',
      targetEngine: 'Self-discovery & Clarity',
      isFeatured: true,
    },
    {
      path: '/apps/whats-next',
      label: "What's Next",
      icon: ArrowRight,
      color: 'primary',
      description: 'The app: Morning Boot on your own items, in your personal workspace. The values and behavior exercises stay here in the Lab.',
      targetEngine: 'Self-discovery & Clarity',
      isFeatured: true,
    },
    {
      path: DEV_PORTS.whatsnext,
      label: "What's Next App",
      icon: ArrowRight,
      color: 'primary',
      description: 'Full career transition platform (local dev :3201) — Get Clear, Build Your Story, pursuit pipeline, and offer evaluation.',
      targetEngine: 'Self-discovery & Clarity',
      isExternal: true,
    },
    {
      path: '/apps/first-100-days',
      label: 'First 100 Days',
      icon: Star,
      color: 'primary',
      description: 'The app: the new-leader journey on your mandate, in your personal workspace (listening tour, priority discovery, Day 101).',
      targetEngine: 'Self-discovery & Clarity',
    },
    
    // Canon Engine
    {
      path: '/elevation/canon-engine',
      label: 'Canon Engine',
      icon: BookOpen,
      color: 'blue',
      description: 'Complete Canon Engine with synthesis, validation, staging, and documentation management',
      targetEngine: 'Canon Engine',
      isFeatured: true
    },
    {
      path: '/elevation/lab/canon',
      label: 'Canon Engine (Lab URL)',
      icon: BookOpen,
      color: 'blue',
      description: 'Same as Canon Engine — legacy /lab/canon path redirects here for bookmarks and deep links.',
      targetEngine: 'Canon Engine',
    },
    {
      path: '/elevation/lab/architecture',
      label: 'System Architecture',
      icon: Network,
      color: 'purple',
      description: 'Explore system architecture diagrams and technical specifications',
      targetEngine: 'Canon Engine',
      isFeatured: false
    },
    {
      path: '/elevation/lab/docs',
      label: 'Documentation',
      icon: FileText,
      color: 'green',
      description: 'Browse and manage system documentation',
      targetEngine: 'Canon Engine',
      isFeatured: false
    },
    {
      path: '/elevation/application-templates',
      label: 'Application Templates',
      icon: Layers,
      color: 'primary',
      description: 'Ready-to-use templates for workshops, assessments, and coaching sessions',
      targetEngine: 'Studio Engine',
      isFeatured: true
    },
    {
      path: VISUAL_LANGUAGE_SERVER_URL,
      label: 'Visual Language Server',
      icon: LayoutGrid,
      color: 'primary',
      description:
        'Standalone server at :3210 — full diagram viewer, patent figures, and all visual-language assets. Start: npm run serve:visual-language',
      isStandalone: true,
      isExternal: true,
    },
    {
      path: VISUAL_LANGUAGE_VIEWER_URL,
      label: 'Visual Language Viewer (bundled)',
      icon: LayoutGrid,
      color: 'charcoal',
      description:
        'Bundled with main SPA — /visual-language/viewer.html. Available without the separate server.',
      isStandalone: true,
      isExternal: true,
    },
    {
      path: '/elevation/lab/geometry-studio',
      label: 'Geometry Studio',
      icon: Shapes,
      color: 'primary',
      description: 'Visual semantic vocabulary builder with faceted search for shapes, lines, fills, and semantic combinations',
      targetEngine: 'Studio Engine',
      isHighlighted: true
    },
    {
      path: '/elevation/icon-customizer',
      label: 'Icon Customizer',
      icon: Palette,
      color: 'primary',
      description: 'Customize and generate icons with different variants, sizes, colors, and styles for the design system',
      targetEngine: 'Studio Engine',
      isHighlighted: true
    },
    {
      path: '/elevation/lab/metaphor-studio',
      label: 'Metaphor Studio',
      icon: Mountain,
      color: 'primary',
      description: 'Experiment with core brand metaphors: mountains, navigation, wayfinding, maps, outdoors, compass, signage, terrain, and geometrics over landscapes',
      targetEngine: 'Studio Engine',
      isHighlighted: true
    },
    {
      path: '/elevation/lab/visual-verbal-integration',
      label: 'Visual-Verbal Integration',
      icon: Eye,
      color: 'primary',
      description: 'Comprehensive system for aligning visual and verbal communication across all formats and contexts',
      targetEngine: 'Studio Engine',
      isHighlighted: true
    },

    // Authoring Engine
    {
      path: '/elevation/lab/gold-rush-template',
      label: 'Gold Rush Template',
      icon: Target,
      color: 'gray',
      description: 'M1 Template for understanding how things change over time across multiple dimensions and milestones',
      targetEngine: 'Authoring Engine'
    },
    {
      path: '/elevation/lab/sentence-builder',
      label: 'Sentence Builder',
      icon: Wand2,
      color: 'gray',
      description: 'Build contextual sentences and get personalized Peak Priorities recommendations based on your situation',
      targetEngine: 'Authoring Engine'
    },

    // Prospector Engine
    {
      path: '/elevation/batch-process-images',
      label: 'Batch Image Processor',
      icon: ImageIcon,
      color: 'primary',
      description: 'Process images from Finder folders or Apple Photos albums. Extract text using OCR, save to Notion, tag with metadata, and archive photos.',
      targetEngine: 'Prospector Engine',
      isFeatured: true
    },
    {
      path: '/elevation/lab/youtube-search',
      label: 'YouTube Prioritization Search',
      icon: Video,
      color: 'primary',
      description: 'Search YouTube videos for prioritization-related content. Search video transcripts for keywords and find relevant content across the platform.',
      targetEngine: 'Prospector Engine',
      isFeatured: false
    },
    {
      path: '/elevation/lab/smart-suggestions',
      label: 'Smart Suggestions',
      icon: Lightbulb,
      color: 'gray',
      description: 'Experimental suggestion engine from M2 template (v1)',
      targetEngine: 'Prospector Engine'
    },
    {
      path: '/elevation/lab/source-of-truth-checker',
      label: 'Source of Truth Checker',
      icon: Cpu,
      color: 'gray',
      description: 'Validate that replicated content (e.g., color names) matches the canonical source.',
      targetEngine: 'Prospector Engine'
    },
    {
      path: '/elevation/lab/relationship-network',
      label: 'Relationship Network',
      icon: Network,
      color: 'gray',
      description:
        'Experimental relationship visualization — explore how concepts and entities connect across the knowledge graph.',
      targetEngine: 'Prospector Engine',
    },
    {
      path: '/elevation/lab/connection-pathfinder',
      label: 'Connection Pathfinder',
      icon: Route,
      color: 'gray',
      description: 'Find meaningful paths between concepts; iterate on graph traversal and connection discovery.',
      targetEngine: 'Prospector Engine',
    },

    // Education Engine
    {
      path: '/elevation/workbook/oqf',
      label: 'Open Questions Framework Workbook',
      icon: BookOpen,
      color: 'primary',
      description: 'Interactive digital workbook for the OQF training - 7 modules with activities, tools, and worksheets to move from uncertainty to clarity',
      targetEngine: 'Education Engine',
      isFeatured: true
    },
    {
      path: '/elevation/lab/semantic-modes',
      label: 'Semantic Reading Modes',
      icon: BookMarked,
      color: 'gray',
      description: 'Normal vs. semantic reading modes with study Bible-style annotations for M1/M0 levels',
      targetEngine: 'Education Engine'
    },
    {
      path: '/elevation/lab/learning-pathways',
      label: 'Learning Pathways',
      icon: BookOpen,
      color: 'gray',
      description: 'Structured learning journeys to master the Peak Priorities system',
      targetEngine: 'Education Engine'
    },

    // Simulation Engine
    {
      path: '/elevation/lab/shape-combinator',
      label: '3D Shape Combinator',
      icon: Dice1,
      color: 'gray',
      description: 'Test all possible logical combinations using 3D shapes with different numbers of faces',
      targetEngine: 'Simulation Engine'
    },

    // Knowledge Engine
    {
      path: DEV_PORTS.objectInspector,
      label: 'Object Inspector',
      icon: Search,
      color: 'primary',
      description: 'Browse, search, edit, and visualize knowledge objects with JQL queries, five-dimensional views, and interactive graph visualization',
      targetEngine: 'Knowledge Engine',
      isExternal: true,
      isFeatured: true
    },
    {
      path: '/elevation/lab/m2-model-taxonomy',
      label: 'M2 Model Taxonomy',
      icon: Layers,
      color: 'gray',
      description: 'Comprehensive categorization of M2 model types including meta-models, product/service offerings, general banking models, and specialized model categories',
      targetEngine: 'Knowledge Engine'
    },
    // Analytics Engine
    {
      path: '/elevation/lab/dashboard',
      label: 'Team Dashboard',
      icon: Activity,
      color: 'gray',
      description: 'Track team activity, stories, and learning progress in real-time',
      targetEngine: 'Analytics Engine'
    },
    {
      path: '/elevation/lab/team-exploration',
      label: 'Team Exploration',
      icon: Users,
      color: 'gray',
      description:
        'Aggregate view of team members exploring models and recent collaboration activity.',
      targetEngine: 'Analytics Engine',
    },
    {
      path: '/elevation/analytics',
      label: 'Analytics Dashboard',
      icon: BarChart3,
      color: 'gray',
      description: 'Comprehensive analytics and insights into your learning journey',
      targetEngine: 'Analytics Engine'
    },
    {
      path: '/elevation/lab/item-detail-third-pass',
      label: 'Item Detail, Third Pass',
      icon: Scale,
      color: 'primary',
      description:
        'The item page organized by what each part is for: six destinations that never disappear, standing that names its list, cycle → track → session with where each setting comes from, AI beside each choice, and a new version when you edit what the item says. Sample item; saves nothing.',
      targetEngine: 'Prioritization Engine',
      isFeatured: true,
    },
    {
      path: '/elevation/lab/item-detail-second-pass',
      label: 'Item Detail, Second Pass',
      icon: Scale,
      color: 'primary',
      description:
        'A working preview of the redesigned item page, with every item ruling from 13 October: additional categories, triage states, the lists an item is in, and the version each group ranked. Sample item; saves nothing.',
      targetEngine: 'Prioritization Engine',
      isFeatured: true,
    },
    {
      path: '/elevation/lab/judgment-index',
      label: 'Judgment Index',
      icon: Scale,
      color: 'primary',
      description:
        'Hartman-assessment profiles for your team — record individual judgment capacity, see team composition, and read prioritization guidance. Advisory only.',
      targetEngine: 'Analytics Engine',
      isFeatured: true,
    },
    {
      path: '/elevation/lab/cognitive-bias',
      label: 'Cognitive Bias Audit',
      icon: Brain,
      color: 'primary',
      description:
        'AI-assisted bias detection across cycle or session context — surfaces how biases manifest in actual prioritization work.',
      targetEngine: 'Analytics Engine',
      isFeatured: true,
    },
    {
      path: '/elevation/lab/lexicon',
      label: 'Lexicon',
      icon: MessagesSquare,
      color: 'primary',
      description:
        'Language intelligence from meeting transcripts — speaker profiles, communication patterns, and baseball-card views.',
      targetEngine: 'Analytics Engine',
      isFeatured: true,
    },
    {
      path: '/elevation/lab/consistency',
      label: 'Consistency Dashboard',
      icon: CheckCircle,
      color: 'primary',
      description: 'Monitor content consistency across your knowledge ecosystem - detect version drift, schema conflicts, and contradictions',
      targetEngine: 'Analytics Engine',
      isFeatured: true
    },

    // Publishing Engine
    {
      path: '/elevation/lab/brand-guidelines-document',
      label: 'Brand Guidelines Document',
      icon: FileText,
      color: 'gray',
      description: 'Comprehensive brand guidelines document covering all aspects of the Peak Priorities visual identity',
      targetEngine: 'Publishing Engine'
    },
    {
      path: '/elevation/lab/business-applications',
      label: 'Business Applications',
      icon: DollarSign,
      color: 'primary',
      description:
        'How the Peak Priorities knowledge system generates revenue — workshops, consulting, artifacts, speaking, and thought leadership offerings.',
      targetEngine: 'Publishing Engine',
      isFeatured: true,
    },

    // Special/System Features
    { 
      path: 'teaching-wizard', 
      label: 'Teaching Wizard', 
      icon: Compass, 
      color: 'gray',
      description: 'Learn how the Peak Priorities system works by experiencing it step-by-step with under-the-hood explanations',
      isSpecial: true
    },
    { 
      path: '/elevation/island', 
      label: 'Island Explorer', 
      icon: MapIcon, 
      color: 'gray',
      description: 'Discover the 12 core prioritization capabilities through interactive exploration',
      isLowPriority: true
    },
    { 
      path: '/elevation/mountain', 
      label: 'Mountain Ascent', 
      icon: Mountain, 
      color: 'gray',
      description: 'Track your progress through the 5 stages of capability development',
      isLowPriority: true
    },
    // Additional System Features
    { 
      path: '/elevation/ai-navigator', 
      label: 'Chatbot', 
      icon: Sparkles, 
      color: 'gray',
      description: 'AI-powered intelligent guide for personalized learning paths'
    },
    { 
      path: 'product-tour', 
      label: 'Product Tour', 
      icon: Compass, 
      color: 'gray',
      description: 'Interactive guided tour of the Peak Priorities system',
      isEventTrigger: true,
      eventName: 'triggerProductTour',
      isStandalone: true
    },
    { 
      path: '/elevation/progress', 
      label: 'Progress & Game Mechanics', 
      icon: TrendingUp, 
      color: 'gray',
      description: 'Gamified progress tracking with achievements and learning paths'
    },
    { 
      path: '/elevation/lab/feature-flags', 
      label: 'Feature Flags', 
      icon: Wrench, 
      color: 'gray',
      description: 'Development tools for managing feature flags and system configuration',
      targetEngine: 'Utilities',
    },

    // Utilities
    {
      path: '/elevation/lab/folder-creator',
      label: 'Folder Creator',
      icon: FolderPlus,
      color: 'primary',
      description: 'Create standardized folder structures for Prospects and Clients in Google Drive. Automates the creation of organized folder hierarchies for sales and client management workflows.',
      targetEngine: 'Utilities'
    },
    
    // Prioritization App Archive
    {
      path: '/elevation/lab/prioritization-archive/security',
      label: 'Security Posture',
      icon: Shield,
      color: 'gray',
      description: 'M2 model evidence tracker for security claims. Validates production readiness claims with supporting evidence.',
      targetEngine: 'Prioritization App Archive'
    },
    {
      path: '/elevation/lab/prioritization-archive/goals',
      label: 'Goal Verification',
      icon: Target,
      color: 'gray',
      description: 'M2 model evidence tracker for goal/strategy verification claims. Validates alignment between priorities and organizational goals.',
      targetEngine: 'Prioritization App Archive'
    },
  ]
    .filter((item) => decisionBriefEnabled || item.path !== DECISION_BRIEF_LAB_PATH)
    .filter((item) => listedFor(elevationOwner, item.path));

  const labItemByPath = new Map(labItems.map(item => [item.path, item]));

  const allLabRouteItems = LAB_ROUTES.map(route => ({
    path: route.path,
    label: labItemByPath.get(route.path)?.label ?? route.label,
  })).sort((a, b) => a.label.localeCompare(b.label));

  const allFeatureItems = [...new Map(
    labItems.map(item => [item.path, item] as const),
  ).values()].sort((a, b) => a.label.localeCompare(b.label));

  // A module-hidden entry is not "still to promote" either, and an owner-only
  // page is not listed for anyone else here either.
  const pendingLabCandidates = visibleLabCandidates([
    ...labItems.map((item) => item.path),
    ...(decisionBriefEnabled ? [] : [DECISION_BRIEF_LAB_PATH]),
  ]).filter((candidate) => listedFor(elevationOwner, candidate.path));
  const pendingCandidatesByCategory = labCandidatesByCategory(pendingLabCandidates);

  if (import.meta.env.DEV) {
    const missingFromItems = labRoutesMissingFromItems(labItems.map(item => item.path)).filter(
      (path) => listedFor(elevationOwner, path),
    );
    if (missingFromItems.length > 0) {
      console.warn('[Lab] Routes in labRoutes.ts but missing from labItems:', missingFromItems);
    }
  }

  const renderCatalogRow = (item: (typeof labItems)[number], compact = false) => {
    const isNew = isLabItemNew(item.path);
    const isUpdated = isLabItemUpdated(item.path);
    const inPrioritiesProduct = isInPrioritiesProduct(item.path);
    const kind = resolveLabCatalogKind(item);
    const KindIcon = kind.icon;
    const content = (
      <>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <KindIcon
            className={`shrink-0 ${kind.iconClass} ${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'}`}
            aria-hidden
            title={kind.label}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
              <span
                className={`font-medium text-charcoal-900 group-hover:text-primary-700 truncate ${
                  compact ? 'text-sm' : ''
                }`}
              >
                {item.label}
              </span>
              {inPrioritiesProduct && (
                <span
                  className="shrink-0 inline-flex items-center gap-0.5 font-mono text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-consulting-slate-blue-50 text-consulting-slate-blue-700 border border-consulting-slate-blue-200"
                  title={PRIORITIES_PRODUCT_SIGNIFIER.label}
                >
                  <Trophy className="h-2.5 w-2.5" aria-hidden />
                  <span className="sr-only">{PRIORITIES_PRODUCT_SIGNIFIER.label}</span>
                  P.ai
                </span>
              )}
              {isNew && (
                <span className="shrink-0 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]">
                  New
                </span>
              )}
              {isUpdated && (
                <span className="shrink-0 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-consulting-slate-blue-100 text-consulting-slate-blue-800 border border-consulting-slate-blue-200">
                  Updated
                </span>
              )}
            </div>
            <div className={`font-mono text-charcoal-500 truncate ${compact ? 'text-[10px]' : 'text-xs'}`}>
              {item.path}
            </div>
          </div>
        </div>
        <ArrowRight
          className={`text-charcoal-400 group-hover:text-primary-600 flex-shrink-0 ${
            compact ? 'h-3.5 w-3.5' : 'h-4 w-4'
          }`}
        />
      </>
    );
    const rowClass = `flex items-center justify-between gap-2 hover:bg-charcoal-50 transition-colors group w-full text-left ${
      compact ? 'px-3 py-2' : 'px-4 py-3 gap-4'
    }${
      isNew
        ? ' bg-[var(--mustard-dim)]/50 border-l-2 border-l-amber-400'
        : isUpdated
          ? ' bg-consulting-slate-blue-50/50 border-l-2 border-l-consulting-slate-blue-400'
          : ''
    }`;

    if (item.isExternal) {
      return (
        <button
          key={item.path}
          type="button"
          onClick={() => window.open(item.path, '_blank', 'noopener,noreferrer')}
          className={rowClass}
        >
          {content}
        </button>
      );
    }

    if ((item as { isEventTrigger?: boolean }).isEventTrigger) {
      return (
        <button
          key={item.path}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.dispatchEvent(
              new CustomEvent((item as { eventName?: string }).eventName ?? 'triggerProductTour', {
                detail: { purpose: item.path, message: item.label },
              }),
            );
          }}
          className={rowClass}
        >
          {content}
        </button>
      );
    }

    if ((item as { isSpecial?: boolean }).isSpecial) {
      return (
        <button
          key={item.path}
          type="button"
          onClick={() => {
            window.dispatchEvent(
              new CustomEvent('triggerTeachingWizard', {
                detail: { purpose: 'teach-system', message: 'Let me show you how this system works...' },
              }),
            );
          }}
          className={rowClass}
        >
          {content}
        </button>
      );
    }

    return (
      <Link key={item.path} to={item.path} className={rowClass}>
        {content}
      </Link>
    );
  };

  const labCatalogIndex = (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-charcoal-900 border-b-2 border-consulting-slate-blue-200 pb-2">
        Complete Lab Index
      </h3>
      <p className="text-sm text-charcoal-600 -mt-2">
        All {allFeatureItems.length} features linked from this page — {allLabRouteItems.length}{' '}
        <code className="text-xs bg-charcoal-100 px-1 rounded">/lab/*</code> routes plus standalone apps,
        coach tools, external dev servers, and event triggers. Sorted A–Z; leading icon indicates kind;{' '}
        <span className="font-mono text-[10px] px-1 py-0.5 rounded bg-consulting-slate-blue-50 border border-consulting-slate-blue-200 text-consulting-slate-blue-700">
          P.ai
        </span>{' '}
        marks priorities.ai product surfaces.
      </p>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        {(() => {
          const colSize = Math.ceil(allFeatureItems.length / 4);
          return [0, 1, 2, 3].map(col => {
            const columnItems = allFeatureItems.slice(col * colSize, (col + 1) * colSize);
            return (
              <div
                key={col}
                className="bg-[var(--bg-raised)] border border-charcoal-200 divide-y divide-charcoal-100"
              >
                {columnItems.map(item => renderCatalogRow(item, true))}
              </div>
            );
          });
        })()}
      </div>
      <div className="border border-charcoal-200 bg-charcoal-50/60 px-4 py-3 space-y-3">
        <div className="text-xs font-semibold text-charcoal-700 uppercase tracking-wide">Legend</div>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {LAB_CATALOG_KINDS.map((kind) => {
            const Icon = kind.icon;
            return (
              <span key={kind.id} className="inline-flex items-center gap-1.5 text-xs text-charcoal-600">
                <Icon className={`h-3.5 w-3.5 shrink-0 ${kind.iconClass}`} aria-hidden />
                {kind.label}
              </span>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1 border-t border-charcoal-200/80">
          <span className="inline-flex items-center gap-1.5 text-xs text-charcoal-600">
            <span className="inline-flex items-center gap-0.5 font-mono text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-consulting-slate-blue-50 text-consulting-slate-blue-700 border border-consulting-slate-blue-200">
              <Trophy className="h-2.5 w-2.5" aria-hidden />
              P.ai
            </span>
            {PRIORITIES_PRODUCT_SIGNIFIER.label}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-charcoal-600">
            <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]">
              New
            </span>
            added in the last 15 days
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-charcoal-600">
            <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-consulting-slate-blue-100 text-consulting-slate-blue-800 border border-consulting-slate-blue-200">
              Updated
            </span>
            touched in the last 15 days
          </span>
        </div>
      </div>
    </div>
  );

  const getColorClasses = (color: string) => {
    const colors: Record<string, any> = {
      primary: {
        bg: 'bg-consulting-slate-blue-50',
        border: 'border-consulting-slate-blue-200',
        text: 'text-consulting-slate-blue-900',
        accent: 'text-consulting-slate-blue-600',
        hover: 'hover:bg-consulting-slate-blue-100'
      },
      gray: {
        bg: 'bg-[var(--surface)]',
        border: 'border-[var(--rule)]',
        text: 'text-[var(--text-pri)]',
        accent: 'text-[var(--text-sec)]',
        hover: 'hover:bg-[var(--hover-tint)]'
      },
      coral: {
        bg: 'bg-coral-50',
        border: 'border-coral-200',
        text: 'text-coral-900',
        accent: 'text-coral-600',
        hover: 'hover:bg-coral-100'
      }
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="h-16 w-16 bg-[var(--mustard)] flex items-center justify-center shadow-md">
            <FlaskConical className="h-10 w-10 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-charcoal-900">
              The Lab
            </h1>
            <p className="text-xl text-charcoal-600">
              Experimental Features & Development Tools
            </p>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <p className="text-lg text-charcoal-700 leading-relaxed">
            Welcome to The Lab, where cutting-edge features and experimental tools are being developed. 
            These applications represent the future of the Peak Priorities knowledge system and provide 
            early access to new capabilities and methodologies.
          </p>
        </div>
      </div>

      {/* Status Badge */}
      <div className="text-center">
        <span className="px-4 py-2 bg-[var(--mustard-dim)] text-[var(--mustard)] text-sm font-semibold rounded-full">
          🧪 All Features in Development
        </span>
      </div>


      {labCatalogIndex}

      {/* Lab Items Grid - Organized by Engine */}
      <div className="space-y-8">
        {/* Standalone Applications */}
        {(() => {
          const standaloneItems = labItems.filter(item => item.isStandalone);
          if (standaloneItems.length === 0) return null;
          
          return (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-charcoal-900 border-b-2 border-consulting-slate-blue-200 pb-2">
                Standalone Applications
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {standaloneItems.map((item) => {
                  const Icon = item.icon;
                  const colors = getColorClasses(item.color);
                  
                  // Build className for the card
                  const cardClassName = `group block p-6  border-2 transition-all duration-200 ${colors.bg} ${colors.border} ${colors.hover} hover:shadow-lg hover:scale-105 ${
                    item.isFeatured ? 'ring-2 ring-consulting-slate-blue-300 ring-opacity-50 shadow-lg' : ''
                  } ${
                    item.isHighlighted ? 'ring-2 ring-amber-300 ring-opacity-50 shadow-lg' : ''
                  } ${
                    item.isLowPriority ? 'opacity-75 hover:opacity-100' : ''
                  }`;
                  
                  // Render event trigger buttons
                  if ((item as any).isEventTrigger) {
                    return (
                      <button
                        key={item.path}
                        onClick={(e) => {
                          e.preventDefault();
                          const event = new CustomEvent((item as any).eventName, { 
                            detail: { 
                              purpose: (item as any).eventName,
                              message: item.label
                            } 
                          });
                          window.dispatchEvent(event);
                        }}
                        className={cardClassName}
                      >
                      <div className="flex items-start space-x-4">
                        <div className={`p-3  ${colors.bg} border ${colors.border} ${
                          item.isFeatured ? 'ring-2 ring-consulting-slate-blue-200' : ''
                        } ${
                          item.isHighlighted ? 'ring-2 ring-amber-200' : ''
                        }`}>
                          <Icon className={`h-6 w-6 ${colors.accent}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className={`text-lg font-semibold ${colors.text} group-hover:underline`}>
                              {item.label}
                            </h3>
                            {item.isFeatured && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-bold rounded-full">
                                ⭐ Featured
                              </span>
                            )}
                            {item.isHighlighted && !(item as any).isFeatured && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-bold rounded-full">
                                Priority
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-charcoal-600 leading-relaxed">
                            {item.description}
                          </p>
                          <div className="mt-3 flex items-center gap-2">
                            {item.isHighlighted && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-medium rounded-full">
                                Priority
                              </span>
                            )}
                            {item.isLowPriority && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs font-medium rounded-full">
                                Low Priority
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                    );
                  }
                  
                  // Render external links with button that opens in new window
                  if (item.isExternal) {
                    return (
                      <button
                        key={item.path}
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(item.path, '_blank', 'noopener,noreferrer');
                        }}
                        className={cardClassName}
                      >
                      <div className="flex items-start space-x-4">
                        <div className={`p-3  ${colors.bg} border ${colors.border} ${
                          item.isFeatured ? 'ring-2 ring-consulting-slate-blue-200' : ''
                        } ${
                          item.isHighlighted ? 'ring-2 ring-amber-200' : ''
                        }`}>
                          <Icon className={`h-6 w-6 ${colors.accent}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className={`text-lg font-semibold ${colors.text} group-hover:underline`}>
                              {item.label}
                            </h3>
                            {item.isFeatured && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-bold rounded-full">
                                ⭐ Featured
                              </span>
                            )}
                            {item.isHighlighted && !(item as any).isFeatured && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-bold rounded-full">
                                Priority
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-charcoal-600 leading-relaxed">
                            {item.description}
                          </p>
                          <div className="mt-3 flex items-center gap-2">
                            {item.isHighlighted && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-medium rounded-full">
                                Priority
                              </span>
                            )}
                            {item.isLowPriority && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs font-medium rounded-full">
                                Low Priority
                              </span>
                            )}
                          </div>
                          {item.isExternal && (
                            <div className="mt-2">
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs font-medium rounded-full">
                                🔗 Opens in new tab
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                    );
                  }
                  
                  // Render internal links with Link component
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cardClassName}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`p-3  ${colors.bg} border ${colors.border} ${
                          item.isFeatured ? 'ring-2 ring-consulting-slate-blue-200' : ''
                        } ${
                          item.isHighlighted ? 'ring-2 ring-amber-200' : ''
                        }`}>
                          <Icon className={`h-6 w-6 ${colors.accent}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className={`text-lg font-semibold ${colors.text} group-hover:underline`}>
                              {item.label}
                            </h3>
                            {item.isFeatured && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-bold rounded-full">
                                ⭐ Featured
                              </span>
                            )}
                            {item.isHighlighted && !(item as any).isFeatured && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-bold rounded-full">
                                Priority
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-charcoal-600 leading-relaxed">
                            {item.description}
                          </p>
                          <div className="mt-3 flex items-center gap-2">
                            {item.isHighlighted && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-medium rounded-full">
                                Priority
                              </span>
                            )}
                            {item.isLowPriority && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs font-medium rounded-full">
                                Low Priority
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Self-discovery & Clarity — Elevation exercises (not Prioritization app features) */}
        {(() => {
          const selfDiscoveryItems = labItems.filter(item => item.targetEngine === 'Self-discovery & Clarity');
          if (selfDiscoveryItems.length === 0) return null;

          return (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-charcoal-900 border-b-2 border-consulting-slate-blue-200 pb-2">
                Self-discovery &amp; Clarity
              </h3>
              <p className="text-sm text-charcoal-600 -mt-2">
                Values, clarity, and career-transition exercises — start here before prioritization.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {selfDiscoveryItems.map((item) => {
                  const Icon = item.icon;
                  const colors = getColorClasses(item.color);
                  const cardClassName = `group block p-6  border-2 transition-all duration-200 ${colors.bg} ${colors.border} ${colors.hover} hover:shadow-lg hover:scale-105 ${
                    item.isFeatured ? 'ring-2 ring-consulting-slate-blue-300 ring-opacity-50 shadow-lg' : ''
                  }`;

                  const cardBody = (
                    <div className="flex items-start space-x-4">
                      <div className={`p-3  ${colors.bg} border ${colors.border}`}>
                        <Icon className={`h-6 w-6 ${colors.accent}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className={`text-lg font-semibold ${colors.text} group-hover:underline`}>
                            {item.label}
                          </h3>
                          {item.isFeatured && (
                            <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-bold rounded-full">
                              ⭐ Featured
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-charcoal-600 leading-relaxed">{item.description}</p>
                        {item.isExternal && (
                          <span className="inline-flex items-center px-2 py-1 mt-2 bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs font-medium rounded-full">
                            Opens in new tab
                          </span>
                        )}
                      </div>
                    </div>
                  );

                  if (item.isExternal) {
                    return (
                      <button
                        key={item.path}
                        type="button"
                        onClick={() => window.open(item.path, '_blank', 'noopener,noreferrer')}
                        className={`${cardClassName} w-full text-left`}
                      >
                        {cardBody}
                      </button>
                    );
                  }

                  return (
                    <Link key={item.path} to={item.path} className={cardClassName}>
                      {cardBody}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Coach's Tools */}
        {(() => {
          const coachItems = labItems.filter(
            (item) => (item as { isCoachTool?: boolean }).isCoachTool,
          );
          if (coachItems.length === 0) return null;

          return (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-charcoal-900 border-b-2 border-[var(--slate)] pb-2">
                Coach&apos;s Tools
              </h3>
              <p className="text-sm text-charcoal-600 -mt-2">
                Field guides and flows for inquiry, delegation, clarity, and the business field.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {coachItems.map((item) => {
                  const Icon = item.icon;
                  const colors = getColorClasses(item.color);
                  const cardClassName = `group block p-6  border-2 transition-all duration-200 ${colors.bg} ${colors.border} ${colors.hover} hover:shadow-lg hover:scale-105 ${
                    item.isFeatured ? 'ring-2 ring-purple-200 ring-opacity-50 shadow-lg' : ''
                  }`;

                  return (
                    <Link key={item.path} to={item.path} className={cardClassName}>
                      <div className="flex items-start space-x-4">
                        <div className={`p-3  ${colors.bg} border ${colors.border}`}>
                          <Icon className={`h-6 w-6 ${colors.accent}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className={`text-lg font-semibold ${colors.text} group-hover:underline`}>
                            {item.label}
                          </h3>
                          <p className="text-sm text-charcoal-600 leading-relaxed mt-1">{item.description}</p>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Utilities Section */}
        {(() => {
          const utilitiesItems = labItems.filter(item => item.targetEngine === 'Utilities');
          if (utilitiesItems.length === 0) return null;
          
          return (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-charcoal-900 border-b-2 border-consulting-slate-blue-200 pb-2">
                Utilities
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {utilitiesItems.map((item) => {
                  const Icon = item.icon;
                  const colors = getColorClasses(item.color);
                  const cardClassName = `group block p-6  border-2 transition-all duration-200 ${colors.bg} ${colors.border} ${colors.hover} hover:shadow-lg hover:scale-105 ${
                    item.isFeatured ? 'ring-2 ring-consulting-slate-blue-300 ring-opacity-50 shadow-lg' : ''
                  }`;
                  
                  if (item.isExternal) {
                    return (
                      <button
                        key={item.path}
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(item.path, '_blank', 'noopener,noreferrer');
                        }}
                        className={cardClassName}
                      >
                        <div className="flex items-start space-x-4">
                          <div className={`p-3  ${colors.bg} border ${colors.border}`}>
                            <Icon className={`h-6 w-6 ${colors.accent}`} />
                          </div>
                          <div className="flex-1">
                            <h3 className={`text-lg font-semibold ${colors.text} mb-2 group-hover:underline`}>
                              {item.label}
                            </h3>
                            <p className="text-sm text-charcoal-600 leading-relaxed">
                              {item.description}
                            </p>
                            <div className="mt-3">
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs font-medium rounded-full">
                                🔗 Opens in new tab
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  }
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cardClassName}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`p-3  ${colors.bg} border ${colors.border}`}>
                          <Icon className={`h-6 w-6 ${colors.accent}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className={`text-lg font-semibold ${colors.text} mb-2 group-hover:underline`}>
                            {item.label}
                          </h3>
                          <p className="text-sm text-charcoal-600 leading-relaxed">
                            {item.description}
                          </p>
                          <div className="mt-3 flex items-center gap-2">
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Group by target engine */}
        {['Canon Engine', 'Studio Engine', 'Authoring Engine', 'Prospector Engine', 'Education Engine', 'Simulation Engine', 'Knowledge Engine', 'Analytics Engine', 'Publishing Engine', 'Prioritization App Archive'].map(engine => {
          const engineItems = labItems.filter(item => item.targetEngine === engine);
          if (engineItems.length === 0) return null;
          
          return (
            <div key={engine} className="space-y-4">
              <h3 className="text-xl font-bold text-charcoal-900 border-b-2 border-consulting-slate-blue-200 pb-2">
                {engine}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {engineItems.map((item) => {
                  const Icon = item.icon;
                  const colors = getColorClasses(item.color);
                  
                  // Build className for the card
                  const cardClassName = `group block p-6  border-2 transition-all duration-200 ${colors.bg} ${colors.border} ${colors.hover} hover:shadow-lg hover:scale-105 ${
                    item.isFeatured ? 'ring-2 ring-consulting-slate-blue-300 ring-opacity-50 shadow-lg' : ''
                  } ${
                    item.isHighlighted ? 'ring-2 ring-amber-300 ring-opacity-50 shadow-lg' : ''
                  } ${
                    item.isLowPriority ? 'opacity-75 hover:opacity-100' : ''
                  }`;
                  
                  // Handle event trigger items
                  if ((item as any).isEventTrigger) {
                    return (
                      <button
                        key={item.path}
                        onClick={(e) => {
                          e.preventDefault();
                          const event = new CustomEvent((item as any).eventName, { 
                            detail: { 
                              purpose: (item as any).eventName,
                              message: item.label
                            } 
                          });
                          window.dispatchEvent(event);
                        }}
                        className={cardClassName}
                      >
                      <div className="flex items-start space-x-4">
                        <div className={`p-3  ${colors.bg} border ${colors.border} ${
                          item.isFeatured ? 'ring-2 ring-consulting-slate-blue-200' : ''
                        } ${
                          item.isHighlighted ? 'ring-2 ring-amber-200' : ''
                        }`}>
                          <Icon className={`h-6 w-6 ${colors.accent}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className={`text-lg font-semibold ${colors.text} group-hover:underline`}>
                              {item.label}
                            </h3>
                            {item.isFeatured && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-bold rounded-full">
                                ⭐ Featured
                              </span>
                            )}
                            {item.isHighlighted && !(item as any).isFeatured && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-bold rounded-full">
                                Priority
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-charcoal-600 leading-relaxed">
                            {item.description}
                          </p>
                          <div className="mt-3 flex items-center gap-2">
                            {item.isHighlighted && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-medium rounded-full">
                                Priority
                              </span>
                            )}
                            {item.isLowPriority && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs font-medium rounded-full">
                                Low Priority
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                    );
                  }
                  
                  // Handle special items that don't navigate to pages
                  if (item.isSpecial) {
                    return (
                      <button
                        key={item.path}
                        onClick={() => {
                          // Trigger the teaching wizard
                          const event = new CustomEvent('triggerTeachingWizard', { 
                            detail: { 
                              purpose: 'teach-system',
                              message: 'Let me show you how this system works...'
                            } 
                          });
                          window.dispatchEvent(event);
                        }}
                        className={`group block p-6  border-2 transition-all duration-200 ${colors.bg} ${colors.border} ${colors.hover} hover:shadow-lg hover:scale-105 w-full text-left`}
                      >
                        <div className="flex items-start space-x-4">
                          <div className={`p-3  ${colors.bg} border ${colors.border}`}>
                            <Icon className={`h-6 w-6 ${colors.accent}`} />
                          </div>
                          <div className="flex-1">
                            <h3 className={`text-lg font-semibold ${colors.text} mb-2 group-hover:underline`}>
                              {item.label}
                            </h3>
                            <p className="text-sm text-charcoal-600 leading-relaxed">
                              {item.description}
                            </p>
                            <div className="mt-3 flex items-center gap-2">
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-medium rounded-full">
                                Development
                              </span>
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-medium rounded-full">
                                Interactive
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  }
                  
                  // Render external links with button that opens in new window
                  if (item.isExternal) {
                    return (
                      <button
                        key={item.path}
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(item.path, '_blank', 'noopener,noreferrer');
                        }}
                        className={cardClassName}
                      >
                      <div className="flex items-start space-x-4">
                        <div className={`p-3  ${colors.bg} border ${colors.border} ${
                          item.isFeatured ? 'ring-2 ring-consulting-slate-blue-200' : ''
                        } ${
                          item.isHighlighted ? 'ring-2 ring-amber-200' : ''
                        }`}>
                          <Icon className={`h-6 w-6 ${colors.accent}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className={`text-lg font-semibold ${colors.text} group-hover:underline`}>
                              {item.label}
                            </h3>
                            {item.isFeatured && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-bold rounded-full">
                                ⭐ Featured
                              </span>
                            )}
                            {item.isHighlighted && !(item as any).isFeatured && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-bold rounded-full">
                                Priority
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-charcoal-600 leading-relaxed">
                            {item.description}
                          </p>
                          <div className="mt-3 flex items-center gap-2">
                            <span className="inline-flex items-center px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-medium rounded-full">
                              Development
                            </span>
                            {item.isHighlighted && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-medium rounded-full">
                                Priority
                              </span>
                            )}
                            {item.isLowPriority && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs font-medium rounded-full">
                                Low Priority
                              </span>
                            )}
                          </div>
                          {item.isExternal && (
                            <div className="mt-2">
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs font-medium rounded-full">
                                🔗 Opens in new tab
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                    );
                  }
                  
                  // Render internal links with Link component
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cardClassName}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`p-3  ${colors.bg} border ${colors.border} ${
                          item.isFeatured ? 'ring-2 ring-consulting-slate-blue-200' : ''
                        } ${
                          item.isHighlighted ? 'ring-2 ring-amber-200' : ''
                        }`}>
                          <Icon className={`h-6 w-6 ${colors.accent}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className={`text-lg font-semibold ${colors.text} group-hover:underline`}>
                              {item.label}
                            </h3>
                            {item.isFeatured && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-bold rounded-full">
                                ⭐ Featured
                              </span>
                            )}
                            {item.isHighlighted && !(item as any).isFeatured && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-bold rounded-full">
                                Priority
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-charcoal-600 leading-relaxed">
                            {item.description}
                          </p>
                          <div className="mt-3 flex items-center gap-2">
                            <span className="inline-flex items-center px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-medium rounded-full">
                              Development
                            </span>
                            {item.isHighlighted && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-medium rounded-full">
                                Priority
                              </span>
                            )}
                            {item.isLowPriority && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs font-medium rounded-full">
                                Low Priority
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* System Features (no target engine) */}
        {(() => {
          const systemItems = labItems.filter(item => !item.targetEngine && !item.isFeatured && !item.isStandalone);
          if (systemItems.length === 0) return null;
          
          return (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-charcoal-900 border-b-2 border-consulting-slate-blue-200 pb-2">
                System Features
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {systemItems.map((item) => {
                  const Icon = item.icon;
                  const colors = getColorClasses(item.color);
                  
                  // Handle special items that don't navigate to pages
                  if (item.isSpecial) {
                    return (
                      <button
                        key={item.path}
                        onClick={() => {
                          // Trigger the teaching wizard
                          const event = new CustomEvent('triggerTeachingWizard', { 
                            detail: { 
                              purpose: 'teach-system',
                              message: 'Let me show you how this system works...'
                            } 
                          });
                          window.dispatchEvent(event);
                        }}
                        className={`group block p-6  border-2 transition-all duration-200 ${colors.bg} ${colors.border} ${colors.hover} hover:shadow-lg hover:scale-105 w-full text-left`}
                      >
                        <div className="flex items-start space-x-4">
                          <div className={`p-3  ${colors.bg} border ${colors.border}`}>
                            <Icon className={`h-6 w-6 ${colors.accent}`} />
                          </div>
                          <div className="flex-1">
                            <h3 className={`text-lg font-semibold ${colors.text} mb-2 group-hover:underline`}>
                              {item.label}
                            </h3>
                            <p className="text-sm text-charcoal-600 leading-relaxed">
                              {item.description}
                            </p>
                            <div className="mt-3 flex items-center gap-2">
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-medium rounded-full">
                                Development
                              </span>
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-medium rounded-full">
                                Interactive
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  }
                  
                  // Build className for the card
                  const cardClassName = `group block p-6  border-2 transition-all duration-200 ${colors.bg} ${colors.border} ${colors.hover} hover:shadow-lg hover:scale-105`;
                  
                  // Render external links with button that opens in new window
                  if (item.isExternal) {
                    return (
                      <button
                        key={item.path}
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(item.path, '_blank', 'noopener,noreferrer');
                        }}
                        className={cardClassName}
                      >
                      <div className="flex items-start space-x-4">
                        <div className={`p-3  ${colors.bg} border ${colors.border}`}>
                          <Icon className={`h-6 w-6 ${colors.accent}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className={`text-lg font-semibold ${colors.text} mb-2 group-hover:underline`}>
                            {item.label}
                          </h3>
                          <p className="text-sm text-charcoal-600 leading-relaxed">
                            {item.description}
                          </p>
                          <div className="mt-3 flex items-center gap-2">
                            <span className="inline-flex items-center px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-medium rounded-full">
                              Development
                            </span>
                            {item.isHighlighted && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-medium rounded-full">
                                Priority
                              </span>
                            )}
                            {item.isLowPriority && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs font-medium rounded-full">
                                Low Priority
                              </span>
                            )}
                          </div>
                          {item.isExternal && (
                            <div className="mt-2">
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs font-medium rounded-full">
                                🔗 Opens in new tab
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                    );
                  }
                  
                  // Render internal links with Link component
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cardClassName}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`p-3  ${colors.bg} border ${colors.border}`}>
                          <Icon className={`h-6 w-6 ${colors.accent}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className={`text-lg font-semibold ${colors.text} mb-2 group-hover:underline`}>
                            {item.label}
                          </h3>
                          <p className="text-sm text-charcoal-600 leading-relaxed">
                            {item.description}
                          </p>
                          <div className="mt-3 flex items-center gap-2">
                            <span className="inline-flex items-center px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-medium rounded-full">
                              Development
                            </span>
                            {item.isHighlighted && (
                              <span className="inline-flex items-center px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 text-xs font-medium rounded-full">
                                Priority
                              </span>
                            )}
                            {item.isLowPriority && (
                              <span className="inline-flex items-center px-2 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs font-medium rounded-full">
                                Low Priority
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Development Notes */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-8 border-2 border-[var(--mustard)]">
        <h2 className="text-2xl font-bold text-[var(--mustard)] mb-4">About The Lab</h2>
        <div className="space-y-4">
          <p className="text-[var(--mustard)] leading-relaxed">
            The Lab is our experimental playground where we develop and test new features before they 
            become part of the core system. All features here are in active development and may change 
            or be removed as we iterate and improve.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-[var(--mustard)] mb-2">What to Expect</h3>
              <ul className="space-y-1 text-sm text-[var(--mustard)]">
                <li>• Cutting-edge features and capabilities</li>
                <li>• Experimental user interfaces</li>
                <li>• Early access to new methodologies</li>
                <li>• Active development and iteration</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-[var(--mustard)] mb-2">Development Status</h3>
              <ul className="space-y-1 text-sm text-[var(--mustard)]">
                <li>• Features may be incomplete or buggy</li>
                <li>• User interfaces may change frequently</li>
                <li>• Data may be reset during development</li>
                <li>• Feedback is welcome and encouraged</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Lab Statistics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-[var(--mustard)]">{allLabRouteItems.length}</div>
            <div className="text-sm text-charcoal-600">Lab Routes</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-consulting-slate-blue-600">{allFeatureItems.length}</div>
            <div className="text-sm text-charcoal-600">Catalog Entries</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-sage-600">100%</div>
            <div className="text-sm text-charcoal-600">In Development</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-charcoal-600">∞</div>
            <div className="text-sm text-charcoal-600">Possibilities</div>
          </div>
        </div>
      </div>

      {/* Lab Candidates — features in the repo not yet in the catalog */}
      {pendingLabCandidates.length > 0 && (
      <div className="bg-[var(--bg-raised)] shadow-sm border border-charcoal-200 p-8">
        <div className="flex items-center space-x-3 mb-2">
          <Search className="h-8 w-8 text-charcoal-500" />
          <h2 className="text-2xl font-bold text-charcoal-900">Lab Candidates</h2>
        </div>
        <p className="text-sm text-charcoal-600 mb-6 max-w-3xl">
          Features registered for promotion but not yet in the main catalog above — prioritization
          insights, sibling apps, standalone tools, and coach frameworks.
        </p>
        <div className="space-y-6">
          {[...pendingCandidatesByCategory.entries()].map(([category, candidates]) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-charcoal-800 uppercase tracking-wide mb-3">
                {category}
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {candidates.map((candidate) => {
                  const row = (
                    <>
                      <div className="min-w-0">
                        <div className="font-medium text-charcoal-900 group-hover:text-primary-700 truncate">
                          {candidate.label}
                        </div>
                        <p className="text-xs text-charcoal-600 mt-0.5 line-clamp-2">
                          {candidate.description}
                        </p>
                        <div className="font-mono text-[10px] text-charcoal-500 truncate mt-1">
                          {candidate.path}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-charcoal-400 group-hover:text-primary-600 flex-shrink-0" />
                    </>
                  );
                  const rowClass =
                    'flex items-center justify-between gap-3 px-4 py-3  border border-charcoal-100 hover:bg-charcoal-50 transition-colors group w-full text-left';

                  if (candidate.external) {
                    return (
                      <button
                        key={candidate.path}
                        type="button"
                        onClick={() => window.open(candidate.path, '_blank', 'noopener,noreferrer')}
                        className={rowClass}
                      >
                        {row}
                      </button>
                    );
                  }

                  return (
                    <Link key={candidate.path} to={candidate.path} className={rowClass}>
                      {row}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-charcoal-500 mt-6">
          {pendingLabCandidates.length} of {LAB_CANDIDATES.length} candidates still to promote — add
          any of these to <code className="bg-charcoal-100 px-1 rounded">labItems</code> in Lab.tsx
          and <code className="bg-charcoal-100 px-1 rounded">labRoutes.ts</code> when ready.
        </p>
      </div>
      )}

      {/* Future Possibilities */}
      <div className="bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 p-8 border-2 border-consulting-slate-blue-200">
        <div className="flex items-center space-x-3 mb-6">
          <Lightbulb className="h-8 w-8 text-consulting-slate-blue-600" />
          <h2 className="text-2xl font-bold text-charcoal-900">Future Possibilities</h2>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-charcoal-900">Enhanced Features</h3>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-charcoal-700">AI-powered relationship discovery and mapping</span>
              </li>
              <li className="flex items-start space-x-2">
                <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-charcoal-700">Dynamic visualization of system architectures</span>
              </li>
              <li className="flex items-start space-x-2">
                <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-charcoal-700">Collaborative editing and version control</span>
              </li>
              <li className="flex items-start space-x-2">
                <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-charcoal-700">Integration with assessment and development tools</span>
              </li>
            </ul>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-charcoal-900">Applications</h3>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-charcoal-700">Organizational capability assessment</span>
              </li>
              <li className="flex items-start space-x-2">
                <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-charcoal-700">Framework recommendation engines</span>
              </li>
              <li className="flex items-start space-x-2">
                <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-charcoal-700">Knowledge graph construction and analysis</span>
              </li>
              <li className="flex items-start space-x-2">
                <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-charcoal-700">Automated content generation and curation</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};